import { useEffect, useRef } from 'react';
import { useImmer } from 'use-immer';
import { getScheduleBundleApi } from '../apis';
import {
  clockOffset,
  computeLocal,
  horizonRefreshDelay,
  msUntilLocalMidnight,
} from '../lib/clock';
import { mapDaySlots } from '../lib/schedule';
import { loadBundle, saveBundle } from '../lib/bundleStore';
import { useLogger } from './useLogger';
import { useNetwork } from './useNetwork';

/**
 * Owns the dated-horizon schedule bundle: restores the last one on boot (offline playback),
 * fetches a fresh one, rolls over midnight locally and re-fetches shortly before it expires.
 * Exposes the current day's slots and the local wall-clock they are evaluated against.
 */
export const useScheduleBundle = ({ enabled, onUnauthorized }) => {
  const [state, setState] = useImmer(() => {
    const stored = loadBundle();
    return {
      bundle: stored?.bundle ?? null,
      clockOffsetMs: stored?.clockOffsetMs ?? 0,
      dayKey: null,
      schedules: [],
      localNow: null, // SERVER_FMT string, re-evaluated at fetch, slot end and midnight
      isFetching: false,
    };
  });
  const { LogError, LogToExternal } = useLogger();

  // Recompute "now" and select the current local day's slots — no network involved.
  const applyCurrentDay = (bundle, offsetMs) => {
    if (!bundle?.days?.length) return;
    const { dayKey, localStr } = computeLocal(offsetMs, bundle.timeZone);
    setState((draft) => {
      draft.dayKey = dayKey;
      draft.schedules = mapDaySlots(bundle, dayKey);
      draft.localNow = localStr;
    });
  };

  const refresh = async () => {
    if (!enabled) return;
    setState((draft) => {
      draft.isFetching = true;
    });
    try {
      // The screen is identified by its access token (sent in the Authorization header).
      const { data: bundle } = await getScheduleBundleApi();
      const offsetMs = clockOffset(bundle.serverTimeUtc);

      setState((draft) => {
        draft.bundle = bundle;
        draft.clockOffsetMs = offsetMs;
      });
      saveBundle(bundle, offsetMs);
      applyCurrentDay(bundle, offsetMs);

      LogError(0, `Schedule Bundle Loaded (${bundle?.days?.length || 0} days)`);
    } catch (error) {
      // 401 = this screen was unpaired/deleted.
      if (error.response?.status === 401) {
        onUnauthorized?.();
        return;
      }
      // Offline or server error: keep playing the bundle we already have.
      LogToExternal('error', error);
      LogError(1, `Exception:refreshSchedule ${error.message}`);
    } finally {
      setState((draft) => {
        draft.isFetching = false;
      });
    }
  };

  // Callers (realtime, timers) always reach the latest refresh, never a stale closure.
  const refreshRef = useRef(refresh);
  refreshRef.current = refresh;
  const requestRefresh = () => refreshRef.current();

  // Boot: play the stored bundle immediately, then fetch a fresh one.
  useEffect(() => {
    if (!enabled) return;
    if (state.bundle) applyCurrentDay(state.bundle, state.clockOffsetMs);
    requestRefresh();
  }, [enabled]);

  // Back online: pick up anything that changed while offline.
  useNetwork({ onOnline: requestRefresh });

  // Local midnight rollover, re-armed whenever the selected day changes.
  useEffect(() => {
    if (!state.bundle || !state.dayKey) return;
    const timer = setTimeout(
      () => applyCurrentDay(state.bundle, state.clockOffsetMs),
      msUntilLocalMidnight(state.clockOffsetMs, state.bundle.timeZone),
    );
    return () => clearTimeout(timer);
  }, [state.bundle, state.dayKey]);

  // Horizon refresh shortly before expiry (with jitter). Replaces the old midnight reload push.
  useEffect(() => {
    if (!enabled || !state.bundle?.expiresAtUtc) return;
    const timer = setTimeout(
      requestRefresh,
      horizonRefreshDelay(state.bundle.expiresAtUtc, state.clockOffsetMs),
    );
    return () => clearTimeout(timer);
  }, [enabled, state.bundle]);

  return {
    bundle: state.bundle,
    schedules: state.schedules,
    localNow: state.localNow,
    // serverUtc - deviceNow: lets clock items show screen-local time on a wrong device clock.
    clockOffsetMs: state.clockOffsetMs,
    isFetching: state.isFetching,
    refresh: requestRefresh,
    // A slot ended: re-evaluate the day (crosses into the next day's slots after midnight).
    reevaluate: () => applyCurrentDay(state.bundle, state.clockOffsetMs),
  };
};
